import { Hide, Show } from "@chakra-ui/react";
import { ReactElement, useCallback, useMemo, useState } from "react";

import { QuotationModel } from "api";
import BackToTopFAB from "components/BackToTopFAB";
import { ComponentTabProps } from "components/Documents/documentsTabs/documentsTabPanels/DocumentsPageTabs";
import DocumentsEmptyState from "components/DocumentsEmptyState";
import FetchMoreWhenInView from "components/FetchMoreWhenInView";
import FileModal from "components/FileModal";
import LoadingSpinner from "components/LoadingSpinner";
import {
  DOCUMENTS_BACK_TO_TOP_MIN_LINES,
  MobileSortableCardTable,
  SortableCardTable,
} from "components/SortableTable";
import { DateColumn } from "components/tableColumns";
import FileDownloadLink from "components/tableColumns/FileDownloadLink";
import { ActiveSort, useRequestParams } from "contexts/RequestParamsProvider";
import useDocumentContractFiles from "hooks/api/documents/useDocumentContractFiles";
import useDocumentContracts from "hooks/api/documents/useDocumentContracts";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { MOBILE_BREAKPOINT_SIZE } from "util/styleHelpers";

export const contractsColumns: ColumnDefinition<QuotationModel>[] = [
  {
    property: "contractNumber",
    sortKey: "ContractNumber",
    columnLabel: "Contract #",
    width: "10ch",
    maxWidth: "10ch",
  },
  {
    property: "poNumber",
    sortKey: "NumAtCard",
    columnLabel: "PO #",
    width: "10ch",
    maxWidth: "13ch",
  },
  {
    property: "contractDate",
    sortKey: "ContractDate",
    columnLabel: "Contract Date",
    width: "11ch",
    maxWidth: "11ch",
  },
  {
    property: "productName",
    sortKey: "SearchItemShortName",
    columnLabel: "Product",
    width: "15ch",
    maxWidth: "15ch",
  },
  {
    property: "status",
    sortKey: "ContractStatus",
    columnLabel: "Status",
    width: "8ch",
    maxWidth: "8ch",
  },
  {
    property: "shipStartDate",
    sortKey: "ShipDateStart",
    columnLabel: "Start Ship",
    width: "11ch",
    maxWidth: "11ch",
  },
  {
    property: "shipEndDate",
    sortKey: "ShipDateEnd",
    columnLabel: "End Ship",
    width: "11ch",
    maxWidth: "11ch",
  },
  {
    property: "quantityTotalFormatted",
    columnLabel: "Total Qty",
    width: "10ch",
    maxWidth: "10ch",
    alignRight: true,
    alwaysFullWidth: true,
  },
  {
    property: "quantityRemainingFormatted",
    columnLabel: "Qty Remaining",
    width: "10ch",
    maxWidth: "10ch",
    alignRight: true,
    alwaysFullWidth: true,
  },
];

const ContractsTable = ({ search }: ComponentTabProps): ReactElement => {
  const selectedCustomerCodes = useSelectedCustomerCode();
  const { activeSort, filters } = useRequestParams();

  const [files, setFiles] = useState<DocumentsFile[]>();
  const {
    data,
    isLoading: contractsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useDocumentContracts(activeSort as ActiveSort<QuotationModel>, {
    cardCodes: selectedCustomerCodes,
    searchString: search,
    ...filters,
  });

  const documentContracts = useMemo(
    () =>
      data?.pages?.map(({ contracts: _contracts }) => _contracts).flat() ?? [],
    [data]
  );

  const { mutateAsync } = useDocumentContractFiles();

  const contractsTableEntries = useMemo(
    () =>
      documentContracts?.map((value) => ({
        ...value,
        contractNumber: (
          <FileDownloadLink
            label={value.contractNumber}
            onFetchFiles={mutateAsync}
            docEntry={value.docEntry}
            onSetFiles={setFiles}
          />
        ),
        quantityTotalFormatted: `${value.quantityTotalFormatted}
        ${value.unitOfMeasure}`,
        quantityRemainingFormatted: `${value.quantityRemainingFormatted}
        ${value.unitOfMeasure}`,
        contractDate: <DateColumn date={value.contractDate} />,
        shipStartDate: <DateColumn date={value.shipStartDate} />,
        shipEndDate: <DateColumn date={value.shipEndDate} />,
      })) ?? [],
    [documentContracts, mutateAsync]
  );

  const onCloseFileModal = useCallback(() => setFiles(undefined), []);

  if (contractsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {documentContracts && documentContracts.length > 0 ? (
        <>
          <FileModal
            files={files}
            onClose={onCloseFileModal}
            isOpen={!!files}
            heading="Contract Files"
          />
          <BackToTopFAB
            isVisible={
              contractsTableEntries.length > DOCUMENTS_BACK_TO_TOP_MIN_LINES
            }
          >
            <Hide below={MOBILE_BREAKPOINT_SIZE}>
              <SortableCardTable
                values={contractsTableEntries}
                columns={contractsColumns}
              />
            </Hide>
            <Show below={MOBILE_BREAKPOINT_SIZE}>
              <MobileSortableCardTable
                values={contractsTableEntries ?? []}
                columns={contractsColumns}
              />
            </Show>
            <FetchMoreWhenInView
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              key={`${data?.pages.length}`}
            />
          </BackToTopFAB>
        </>
      ) : (
        <DocumentsEmptyState />
      )}
    </>
  );
};

export default ContractsTable;
